// #2782 — the Classes screen's (ClassManager.jsx) Group by / Sort by pickers
// remember the operator's last manual pick in localStorage, so a reload or a
// navigate-away-and-back opens the list grouped and sorted the way they left
// it instead of snapping back to the defaults (bugs board #256).
//
// Same pattern as this app's placingViewPref.js (#1595) and rideViewMode.js
// (#2012): namespaced keys, try/catch'd get/set so a browser with storage
// disabled (private mode) degrades to "no preference" instead of throwing,
// and a stored value a build no longer recognizes reads back as null so the
// caller falls through to its own default rather than handing an
// unrecognized value to the grouping/sort code. Per-device display
// preference, not part of the user/account record — it deliberately does not
// follow an admin across devices.
//
// The getters return null (not the default) when nothing valid is stored:
// only a MANUAL pick is ever written (ClassManager's onChange handlers), so
// an auto-applied default can never leak into localStorage, same discipline
// as placingViewPref.js.

export const CLASS_LIST_GROUP_BY = {
  NONE: "none",
  DAY: "day",
  RING: "ring",
  DAY_RING: "day-ring",
};

export const CLASS_LIST_SORT_BY = {
  NUMBER: "number",
  NAME: "name",
  TIME: "time",
};

// The first-ever-visit defaults (AC3): ring grouping, number sort — the same
// values ClassManager.jsx's useState initializers carried before #2782.
export const DEFAULT_CLASS_LIST_GROUP_BY = CLASS_LIST_GROUP_BY.RING;
export const DEFAULT_CLASS_LIST_SORT_BY = CLASS_LIST_SORT_BY.NUMBER;

const GROUP_BY_STORAGE_KEY = "scoreadmin-fe.classListGroupBy";
const SORT_BY_STORAGE_KEY = "scoreadmin-fe.classListSortBy";

/**
 * @returns {string|null} the operator's last stored Group by pick when it is
 * a value CLASS_LIST_GROUP_BY still recognizes; null when unset, unreadable,
 * or a garbage/stale value — the caller falls through to
 * DEFAULT_CLASS_LIST_GROUP_BY.
 */
export function getStoredClassListGroupBy() {
  try {
    const raw = localStorage.getItem(GROUP_BY_STORAGE_KEY);
    return Object.values(CLASS_LIST_GROUP_BY).includes(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** @param {string} value - a CLASS_LIST_GROUP_BY value; anything else is ignored. */
export function storeClassListGroupBy(value) {
  if (!Object.values(CLASS_LIST_GROUP_BY).includes(value)) return;
  try {
    localStorage.setItem(GROUP_BY_STORAGE_KEY, value);
  } catch {
    // Storage unavailable (private browsing) — non-fatal; the pick just
    // won't survive the reload.
  }
}

/**
 * @returns {string|null} the operator's last stored Sort by pick when it is a
 * value CLASS_LIST_SORT_BY still recognizes; null when unset, unreadable, or
 * a garbage/stale value — the caller falls through to
 * DEFAULT_CLASS_LIST_SORT_BY.
 */
export function getStoredClassListSortBy() {
  try {
    const raw = localStorage.getItem(SORT_BY_STORAGE_KEY);
    return Object.values(CLASS_LIST_SORT_BY).includes(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** @param {string} value - a CLASS_LIST_SORT_BY value; anything else is ignored. */
export function storeClassListSortBy(value) {
  if (!Object.values(CLASS_LIST_SORT_BY).includes(value)) return;
  try {
    localStorage.setItem(SORT_BY_STORAGE_KEY, value);
  } catch {
    // Storage unavailable (private browsing) — non-fatal; the pick just
    // won't survive the reload.
  }
}
